import { CurrentBrand, Connections } from 'modules/Types';
import { SocialConnectType } from 'modules/Const';

/** Priority levels for different connection types */
export enum ConnectionPriority {
    CRITICAL = 'critical',    // Essential for basic functionality
    IMPORTANT = 'important',  // Enhances capabilities significantly  
    OPTIONAL = 'optional'     // Nice to have but not essential
}

/** Status of individual platform connections */
export enum ConnectionStatus {
    CONNECTED = 'connected',
    MISSING = 'missing',
    NEEDS_SETUP = 'needs_setup',     // Connected but needs additional configuration
    VERIFICATION_FAILED = 'verification_failed'  // Connected but verification failed
}

/** Individual platform connection information */
export interface PlatformConnection {
    platform: SocialConnectType;
    name: string;
    status: ConnectionStatus;
    priority: ConnectionPriority;
    /** Additional details about connection status */
    details?: string;
    /** Username or identifier if connected */
    username?: string;
    /** Profile image if available */
    image?: string;
    /** URL to profile if available */
    url?: string;
    /** Follower count if available */
    followers?: number;
    /** Whether this connection supports verification */
    supportsVerification?: boolean;
    /** Verification status for complex connections like Facebook */
    verificationStatus?: {
        isVerified: boolean;
        issues?: string[];
        requirements?: string[];
    };
}

/** Overall connection status for a brand */
export interface BrandConnectionStatus {
    /** Brand information */
    brandId: number;
    brandName: string;
    /** All platform connections */
    platforms: PlatformConnection[];
    /** Critical connections that are missing */
    missingCriticalConnections: PlatformConnection[];
    /** Important connections that are missing */
    missingImportantConnections: PlatformConnection[];
    /** Connections that need additional setup */
    needsSetupConnections: PlatformConnection[];
    /** Overall completion percentage */
    completionPercentage: number;
    /** Whether brand can run ads */
    canRunAds: boolean;
    /** Facebook-specific ad readiness */
    facebookAdReadiness: {
        canRunFacebookAds: boolean;
        canRunInstagramAds: boolean;
        hasConversionsSetup: boolean;
        issues: string[];
    };
    /** TikTok-specific ad readiness */
    tiktokAdReadiness: {
        canRunTikTokAds: boolean;
        issues: string[];
    };
}

/** Configuration for each platform */
interface PlatformConfig {
    name: string;
    priority: ConnectionPriority;
    supportsVerification?: boolean;
    getConnectionStatus: (connections: Connections) => {
        status: ConnectionStatus;
        details?: string;
        username?: string;
        image?: string;
        url?: string;
        followers?: number;
        verificationStatus?: {
            isVerified: boolean;
            issues?: string[];
            requirements?: string[];
        };
    };
}

/** Platform configurations mapping */
const PLATFORM_CONFIGS: Record<SocialConnectType, PlatformConfig> = {
    [SocialConnectType.SPOTIFY]: {
        name: 'Spotify',
        priority: ConnectionPriority.CRITICAL,
        getConnectionStatus: (connections) => {
            const spotify = connections.spotify_artist_page;
            if (!spotify) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: spotify.name,
                image: spotify.images?.[0]?.url,
                url: spotify.external_urls?.spotify,
                followers: spotify.followers?.total
            };
        }
    },
    [SocialConnectType.FACEBOOK]: {
        name: 'Facebook',
        priority: ConnectionPriority.CRITICAL,
        supportsVerification: true,
        getConnectionStatus: (connections) => {
            const fbPage = connections.facebook_page;
            const fbUser = connections.logged_in_fb_user;
            const fbAdAccount = connections.facebook_ad_account;
            const fbPixel = connections.facebook_pixel;
            
            if (!fbUser) {
                return { 
                    status: ConnectionStatus.MISSING,
                    details: 'Facebook login required'
                };
            }

            if (!fbPage) {
                return { 
                    status: ConnectionStatus.NEEDS_SETUP,
                    details: 'Facebook page not connected'
                };
            }

            // Check verification status
            const issues: string[] = [];
            const requirements: string[] = [];

            if (!fbAdAccount) {
                issues.push('No ad account connected');
                requirements.push('Connect a Facebook ad account to run ads');
            } else {
                // Check ad account health based on existing logic
                if (!fbAdAccount.userHasAccess) {
                    issues.push('Insufficient ad account permissions');
                    requirements.push('Enable "Manage" and "View Performance" permissions');
                }
                if (!fbAdAccount.hasValidAccountStatus) {
                    issues.push('Ad account is not active');
                    requirements.push('Ensure ad account is active in Facebook Business Manager');
                }
                if (!fbAdAccount.hasFundingSource) {
                    issues.push('No payment method configured');
                    requirements.push('Add payment method to ad account');
                }
            }

            if (!fbPixel) {
                issues.push('No Facebook pixel connected');
                requirements.push('Connect Facebook pixel for conversion tracking');
            }

            const isVerified = issues.length === 0;
            const status = issues.length === 0 ? 
                ConnectionStatus.CONNECTED : 
                ConnectionStatus.NEEDS_SETUP;

            return {
                status,
                username: fbPage.name,
                image: fbPage.picture?.data?.url,
                followers: fbPage.followers_count,
                verificationStatus: {
                    isVerified,
                    issues: issues.length > 0 ? issues : undefined,
                    requirements: requirements.length > 0 ? requirements : undefined
                }
            };
        }
    },
    [SocialConnectType.INSTAGRAM]: {
        name: 'Instagram',
        priority: ConnectionPriority.CRITICAL,
        getConnectionStatus: (connections) => {
            const igPage = connections.instagram_page;
            const businessDiscovery = connections.business_discovery_instagram;
            const fbUser = connections.logged_in_fb_user;
            
            // Check for authenticated Instagram (via Facebook)
            if (igPage) {
                return {
                    status: ConnectionStatus.CONNECTED,
                    username: igPage.instagramAccount?.username,
                    image: igPage.instagramAccount?.profile_picture_url,
                    followers: igPage.instagramAccount?.followed_by_count,
                    details: 'Connected via Facebook (can run ads)'
                };
            }
            
            // Check for business discovery (username only)
            if (businessDiscovery) {
                const canRunAds = Boolean(fbUser);
                return {
                    status: canRunAds ? ConnectionStatus.CONNECTED : ConnectionStatus.NEEDS_SETUP,
                    username: businessDiscovery.username,
                    image: businessDiscovery.profile_picture_url,
                    followers: businessDiscovery.followers_count,
                    details: canRunAds ? 
                        'Connected (can run ads)' : 
                        'Username only (connect Facebook to run ads)'
                };
            }
            
            return { status: ConnectionStatus.MISSING };
        }
    },
    [SocialConnectType.TIKTOK]: {
        name: 'TikTok',
        priority: ConnectionPriority.IMPORTANT,
        supportsVerification: true,
        getConnectionStatus: (connections) => {
            const tiktok = connections.tiktok;
            const tiktokAuth = connections.tiktok_auth;
            const tiktokAdAccount = connections.tiktok_ad_account;
            
            if (!tiktok) {
                return { status: ConnectionStatus.MISSING };
            }

            const issues: string[] = [];
            const requirements: string[] = [];

            // Check for business authentication
            if (!tiktokAuth) {
                issues.push('TikTok business account not connected');
                requirements.push('Connect TikTok for Business to run ads');
            }

            // Check for ad account
            if (!tiktokAdAccount) {
                issues.push('No TikTok ad account');
                requirements.push('Set up TikTok ad account for advertising');
            } else if (!connections.tiktok_ad_account_funded) {
                issues.push('Ad account not funded');
                requirements.push('Add payment method to TikTok ad account');
            }

            const isVerified = issues.length === 0;
            const status = issues.length === 0 ? 
                ConnectionStatus.CONNECTED : 
                ConnectionStatus.NEEDS_SETUP;

            return {
                status,
                username: tiktok.username,
                verificationStatus: {
                    isVerified,
                    issues: issues.length > 0 ? issues : undefined,
                    requirements: requirements.length > 0 ? requirements : undefined
                }
            };
        }
    },
    [SocialConnectType.YOUTUBE]: {
        name: 'YouTube',
        priority: ConnectionPriority.IMPORTANT,
        getConnectionStatus: (connections) => {
            const youtube = connections.youtube_channel;
            if (!youtube) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: youtube.snippet?.title,
                image: youtube.snippet?.thumbnails?.medium?.url,
                url: `https://youtube.com/channel/${youtube.id.channelId}`
            };
        }
    },
    [SocialConnectType.SOUNDCLOUD]: {
        name: 'SoundCloud',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const soundcloud = connections.soundcloud_artist_page;
            if (!soundcloud) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: soundcloud.username,
                image: soundcloud.thumbnail_url,
                followers: soundcloud.followers
            };
        }
    },
    [SocialConnectType.AUDIOMACK]: {
        name: 'Audiomack',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const audiomack = connections.audiomack;
            if (!audiomack) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: audiomack.username,
                image: audiomack.thumbnail_url,
                followers: audiomack.followers
            };
        }
    },
    [SocialConnectType.DEEZER]: {
        name: 'Deezer',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const deezer = connections.deezer;
            if (!deezer) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: deezer.name,
                image: deezer.thumbnail_url,
                url: deezer.permalink_url,
            };
        }
    },
    [SocialConnectType.TWITTER]: {
        name: 'Twitter/X',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const twitter = connections.twitter;
            if (!twitter) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                username: twitter.screen_name,
                url: twitter.url
            };
        }
    },
    [SocialConnectType.WEBSITE]: {
        name: 'Website',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const website = connections.website;
            if (!website) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                url: website.url
            };
        }
    },
    [SocialConnectType.NEWSLETTER]: {
        name: 'Newsletter',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const newsletter = connections.newsletter;
            if (!newsletter) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                url: newsletter.url
            };
        }
    },
    [SocialConnectType.SHOPIFY]: {
        name: 'Shopify',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const shopify = connections.shopify;
            if (!shopify) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                url: shopify.url
            };
        }
    },
    [SocialConnectType.ETSY]: {
        name: 'Etsy',
        priority: ConnectionPriority.OPTIONAL,
        getConnectionStatus: (connections) => {
            const etsy = connections.etsy;
            if (!etsy) {
                return { status: ConnectionStatus.MISSING };
            }
            return {
                status: ConnectionStatus.CONNECTED,
                url: etsy.url
            };
        }
    }
};

/**
 * Analyzes a brand's connection status across all platforms
 */
export const getBrandConnectionStatus = (brand: CurrentBrand): BrandConnectionStatus => {
    const platforms: PlatformConnection[] = [];
    const missingCriticalConnections: PlatformConnection[] = [];
    const missingImportantConnections: PlatformConnection[] = [];
    const needsSetupConnections: PlatformConnection[] = [];

    // Analyze each platform
    Object.entries(PLATFORM_CONFIGS).forEach(([platformKey, config]) => {
        const platform = platformKey as SocialConnectType;
        const connectionInfo = config.getConnectionStatus(brand.connections);
        
        const platformConnection: PlatformConnection = {
            platform,
            name: config.name,
            status: connectionInfo.status,
            priority: config.priority,
            details: connectionInfo.details,
            username: connectionInfo.username,
            image: connectionInfo.image,
            url: connectionInfo.url,
            followers: connectionInfo.followers,
            supportsVerification: config.supportsVerification,
            verificationStatus: connectionInfo.verificationStatus
        };

        platforms.push(platformConnection);

        // Categorize missing/incomplete connections
        if (connectionInfo.status === ConnectionStatus.MISSING) {
            if (config.priority === ConnectionPriority.CRITICAL) {
                missingCriticalConnections.push(platformConnection);
            } else if (config.priority === ConnectionPriority.IMPORTANT) {
                missingImportantConnections.push(platformConnection);
            }
        } else if (connectionInfo.status === ConnectionStatus.NEEDS_SETUP) {
            needsSetupConnections.push(platformConnection);
        }
    });

    // Calculate completion percentage
    const totalPlatforms = platforms.length;
    const connectedPlatforms = platforms.filter(p => 
        p.status === ConnectionStatus.CONNECTED
    ).length;
    const completionPercentage = Math.round((connectedPlatforms / totalPlatforms) * 100);

    // Analyze ad readiness
    const facebookPlatform = platforms.find(p => p.platform === SocialConnectType.FACEBOOK);
    const instagramPlatform = platforms.find(p => p.platform === SocialConnectType.INSTAGRAM);
    const tiktokPlatform = platforms.find(p => p.platform === SocialConnectType.TIKTOK);

    const facebookAdReadiness = {
        canRunFacebookAds: facebookPlatform?.verificationStatus?.isVerified || false,
        canRunInstagramAds: (
            facebookPlatform?.verificationStatus?.isVerified && 
            instagramPlatform?.status === ConnectionStatus.CONNECTED
        ) || false,
        hasConversionsSetup: Boolean(brand.connections.facebook_pixel),
        issues: [
            ...(facebookPlatform?.verificationStatus?.issues || []),
            ...(instagramPlatform?.status === ConnectionStatus.MISSING ? ['Instagram not connected'] : [])
        ]
    };

    const tiktokAdReadiness = {
        canRunTikTokAds: tiktokPlatform?.verificationStatus?.isVerified || false,
        issues: tiktokPlatform?.verificationStatus?.issues || []
    };

    const canRunAds = facebookAdReadiness.canRunFacebookAds || tiktokAdReadiness.canRunTikTokAds;

    return {
        brandId: brand.id,
        brandName: brand.name,
        platforms,
        missingCriticalConnections,
        missingImportantConnections,
        needsSetupConnections,
        completionPercentage,
        canRunAds,
        facebookAdReadiness,
        tiktokAdReadiness
    };
};

/**
 * Gets platforms that need immediate attention (critical missing or needs setup)
 */
export const getPlatformsNeedingAttention = (status: BrandConnectionStatus): PlatformConnection[] => {
    return [
        ...status.missingCriticalConnections,
        ...status.needsSetupConnections
    ].sort((a, b) => {
        // Sort by priority (critical first)
        if (a.priority !== b.priority) {
            const priorityOrder = {
                [ConnectionPriority.CRITICAL]: 0,
                [ConnectionPriority.IMPORTANT]: 1,
                [ConnectionPriority.OPTIONAL]: 2
            };
            return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        // Then by platform name
        return a.name.localeCompare(b.name);
    });
};

/**
 * Gets a summary of connection health across multiple brands
 */
export const getTeamConnectionsSummary = (brands: CurrentBrand[]) => {
    const brandStatuses = brands.map(getBrandConnectionStatus);
    
    const totalBrands = brandStatuses.length;
    const fullyConnectedBrands = brandStatuses.filter(
        status => status.missingCriticalConnections.length === 0
    ).length;
    const brandsCanRunAds = brandStatuses.filter(
        status => status.canRunAds
    ).length;
    
    const avgCompletionPercentage = totalBrands > 0 ? 
        Math.round(brandStatuses.reduce((sum, status) => sum + status.completionPercentage, 0) / totalBrands) : 
        0;

    // Count platforms needing attention across all brands
    const platformsNeedingAttention = brandStatuses.reduce((total, status) => {
        return total + getPlatformsNeedingAttention(status).length;
    }, 0);

    return {
        totalBrands,
        fullyConnectedBrands,
        brandsCanRunAds,
        avgCompletionPercentage,
        platformsNeedingAttention,
        brandStatuses
    };
};